// The boot error painter (R177).
//
// A throw before a page has put anything on the screen used to leave the
// page blank: the document held nothing, and the eye saw only the inline
// background of the HTML file. On a telephone nobody reads a console, so
// the fault looked like a page that was still loading.
//
// This module installs one `error` handler and one `unhandledrejection`
// handler on the window when it evaluates, and each handler writes the
// message into a fixed panel over the background. The boot module of every
// Vite page imports it FIRST, so its handlers stand before any other module
// of the page evaluates, the language load included (R1429;
// `booterror.test.ts` holds that rule for every page).
//
// The rules:
//
// - **It imports nothing.** A module it imported could throw before the
//   handlers stand, and that throw is the one case the painter exists for.
//   For the same reason it does not wait for a stylesheet: the panel states
//   its own box, and every colour reads the chrome token first and falls
//   back to that token's value when `styles.css` never arrived.
// - **It writes no sentence.** The panel shows the browser's own message
//   and the stack. A sentence would need `gen/strings.ts`, which is an
//   import (see above).
// - **It paints only while the page boots.** The entry calls
//   `bootSettled()` when its first screen stands. The handlers then come
//   off, and a later fault goes to the console as before, so a fault in a
//   running match never covers the map.
// - **The policy stays as it is.** The module is a part of the page's own
//   bundle, so no page needs an inline script, and it sets the style through
//   the CSSOM, which `style-src` does not govern.

/** The id of the panel. */
export const BOOT_ERROR_ID = 'booterror';

/** The panel shows at most this many faults. A throw in a loop must not
 * grow the document without end. */
const MAX_FAULTS = 5;

/** The number of stack lines the panel keeps under each message. */
const STACK_LINES = 8;

/** Chrome reports a benign layout notice through `window.onerror`. It is not
 * a fault, and it must not cover a page that works. */
const BENIGN = /^ResizeObserver loop/;

/** The text of one fault: the message, then the first lines of the stack. */
export function faultText(err: unknown, fallback = ''): string {
  if (err instanceof Error) {
    const head = `${err.name}: ${err.message}`;
    const stack = (err.stack ?? '')
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l !== '' && !l.startsWith(head))
      .slice(0, STACK_LINES);
    return [head, ...stack].join('\n');
  }
  if (err !== undefined && err !== null) {
    const s = typeof err === 'string' ? err : safeString(err);
    if (s !== '') return s;
  }
  return fallback;
}

function safeString(v: unknown): string {
  try {
    return String(v);
  } catch {
    return '';
  }
}

/** The faults already on the panel, so one fault paints once. */
const shown = new Set<string>();

/**
 * Writes one fault into the panel, and builds the panel on the first call.
 * It never throws: a painter that throws from inside an error handler
 * reports nothing at all.
 */
function paintBootError(text: string, doc: Document = document): void {
  try {
    if (text === '' || BENIGN.test(text) || shown.has(text)) return;
    if (shown.size >= MAX_FAULTS) return;
    shown.add(text);
    let panel = doc.getElementById(BOOT_ERROR_ID);
    if (panel === null) {
      panel = doc.createElement('div');
      panel.id = BOOT_ERROR_ID;
      panel.setAttribute('role', 'alert');
      const st = panel.style;
      st.position = 'fixed';
      st.left = '16px';
      st.right = '16px';
      st.top = '16px';
      st.maxHeight = 'calc(100vh - 32px)';
      st.overflow = 'auto';
      st.boxSizing = 'border-box';
      st.padding = '12px 40px 12px 14px';
      // Over everything the pages build: `#fatal` stands at 100.
      st.zIndex = '2147483647';
      st.background = 'rgb(var(--deep-rgb, 2 11 18) / 0.94)';
      st.border = '1px solid rgb(var(--bad-rgb, 255 92 138) / 0.6)';
      st.color = 'var(--text, #a3cfe6)';
      st.font = '12px/1.45 var(--mono, ui-monospace, monospace)';
      st.whiteSpace = 'pre-wrap';
      st.overflowWrap = 'anywhere';
      st.userSelect = 'text';
      st.pointerEvents = 'auto';

      // The ✕ takes the panel away: a fault on a page that still works
      // must not hide it for good.
      const close = doc.createElement('button');
      close.type = 'button';
      close.textContent = '✕';
      const cs = close.style;
      cs.position = 'absolute';
      cs.top = '6px';
      cs.right = '6px';
      cs.width = '28px';
      cs.height = '28px';
      cs.border = '0';
      cs.background = 'transparent';
      cs.color = 'inherit';
      cs.font = 'inherit';
      cs.cursor = 'pointer';
      close.addEventListener('click', () => panel?.remove());
      panel.appendChild(close);

      (doc.body ?? doc.documentElement).appendChild(panel);
    }
    const line = doc.createElement('div');
    line.style.color = 'var(--bad, #ff5c8a)';
    line.style.marginTop = panel.childElementCount > 1 ? '10px' : '0';
    // textContent and never markup: a message can carry anything.
    line.textContent = text;
    panel.appendChild(line);
  } catch {
    /* nothing is left to report to */
  }
}

function onError(ev: Event): void {
  const e = ev as ErrorEvent;
  // A script of another origin, such as an iOS share extension, reports a
  // fault with no file and no error object. The page cannot act on it.
  if (!e.filename && !e.error && /^Script error\.?$/.test(e.message ?? '')) return;
  const where = e.filename ? `\n${e.filename}:${e.lineno ?? 0}:${e.colno ?? 0}` : '';
  paintBootError(faultText(e.error, `${e.message ?? ''}${where}`));
}

function onRejection(ev: Event): void {
  paintBootError(faultText((ev as PromiseRejectionEvent).reason, 'unhandled rejection'));
}

let installed = false;

/** Puts the two handlers on the window. The module calls it when it
 * evaluates; a second call does nothing. */
export function installBootPainter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
}

/** The page's first screen stands: the handlers come off, and a later fault
 * goes to the console only. A panel already painted stays. */
export function bootSettled(): void {
  if (!installed || typeof window === 'undefined') return;
  installed = false;
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onRejection);
}

/** The panel's memory, for the tests. */
export function resetBootPainter(): void {
  shown.clear();
}

installBootPainter();
